'use client';

import type { Data } from '@strapi/types';
import { type FunctionComponent, useState } from 'react';

import { useAppContext } from '@/modules/app-context/use-app-context';
import { useDevice } from '@/modules/utils/custom-hooks/use-device';
import { useDropdownDateOptions } from '@/modules/utils/custom-hooks/use-dropdown-date-options';
import { Card } from '@/ui-components/card/card';
import { SearchableDropdown } from '@/ui-components/dropdown/searchable-dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Paragraph } from '@/ui-components/paragraph/paragraph';

import { useProjectHistoryParams } from './hooks/use-project-history-params';
import { VersionsList } from './versions-list';

export const ProjectHistory: FunctionComponent = () => {
  const { selectedDocument, setSelectedDocument } = useAppContext();

  const [date, setDate] = useState<string[] | null>(null);
  const [selectedToolName, setSelectedToolName] = useState<Pick<Data.Entity<'api::document-type.document-type'>, 'title'>['title']>(null);
  const { dateOptions } = useDropdownDateOptions();
  const { isLaptop } = useDevice();
  const { t, historyItems, error, isLoading, allFilters, project, toolsForSelect } = useProjectHistoryParams({
    projectDocumentId: selectedDocument.documentId,
    selectedToolName,
    date
  });

  return (
    <Flexbox direction='col' className='h-full'>
      <Paragraph variant='text-base-medium' className='mb-2'>
        {!isLaptop && <span>{project?.title ? `${project?.title} ${t('T_HISTORY')}` : t('T_PROJECT_HISTORY')}</span>}
      </Paragraph>
      <Card fullWidth fullHeight className='dark:bg-card_dark'>
        <Flexbox direction='col' className='relative h-full'>
          <Flexbox direction='col'>
            <Flexbox>
              <SearchableDropdown
                className='w-full'
                sizing='small'
                options={allFilters ?? []}
                disabled={isLoading || !project}
                onSelect={(value) => !Array.isArray(value) && setSelectedDocument({ documentId: value })}
                placeholder={t('T_ALL_DOCUMENTS')}
                onClear={() => setSelectedDocument({ documentId: '', name: '' })}
                externalSearchTerm={selectedDocument.name ?? ''}
              />
            </Flexbox>

            <Flexbox justify='between' className='flex-wrap mt-2 xx-large:mb-2'>
              <Flexbox className='w-full flex-col pb-0 xx-large:flex-row'>
                <SearchableDropdown
                  className='mb-2 xx-large:w-3/6 xx-large:pr-1 xx-large:mb-0'
                  sizing='small'
                  onSelect={(value) => {
                    Array.isArray(value) && setDate(value);
                  }}
                  hasDatePicker
                  disabled={isLoading || !project}
                  options={dateOptions}
                  placeholder={t('T_BY_PERIOD')}
                  onClear={() => setDate(null)}
                />
                <Flexbox className='w-full mb-2 justify-between xx-large:w-3/6 xx-large:mb-0'>
                  <SearchableDropdown
                    className='w-full pr-0 xx-large:w-3/6 xx-large:pr-1 xx-large:mb-0'
                    sizing='small'
                    options={toolsForSelect}
                    disabled={isLoading || !project}
                    onSelect={(value) => !Array.isArray(value) && setSelectedToolName(value)}
                    placeholder={t('T_BY_TOOL')}
                    onClear={() => setSelectedToolName(null)}
                  />
                </Flexbox>
              </Flexbox>
            </Flexbox>
          </Flexbox>
          <VersionsList error={error} historyItems={historyItems} isLoading={isLoading} />
        </Flexbox>
      </Card>
    </Flexbox>
  );
};
